import { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { AuthContext } from '@/App';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useDateRange } from '@/contexts/DateRangeContext';
import { useTheme } from '@/contexts/ThemeContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Activity, AlertTriangle, TrendingUp, TrendingDown, Zap,
  Target, Users, DollarSign, Sparkles, ArrowRight, CheckCircle2
} from 'lucide-react';
import { mockHealthData, getScoreColor, getScoreLabel } from '@/data/mockHealthData';

export default function HealthScore() {
  const { token } = useContext(AuthContext);
  const { activeWorkspace } = useWorkspace();
  const theme = useTheme();
  const { range } = useDateRange();
  const dateRange = range?.preset || '30d';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [updatingIndustry, setUpdatingIndustry] = useState(false);
  const [platform, setPlatform] = useState('all');

  useEffect(() => {
    const clientId = activeWorkspace?.id;
    if (!clientId || !token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        const params = new URLSearchParams({ client_id: clientId });
        if (range?.from) params.append('date_from', range.from);
        if (range?.to) params.append('date_to', range.to);
        if (platform && platform !== 'all') params.append('platform', platform);
        const res = await fetch(`${BACKEND_URL}/api/ai/health-score?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const json = await res.json();
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      } catch (err) {
        console.error('Health score fetch error:', err);
        if (!cancelled) {
          setData(mockHealthData);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token, activeWorkspace?.id, range?.from, range?.to, platform]);

  // Cargar lista de industrias al montar
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${BACKEND_URL}/api/industries`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setIndustries(await res.json());
      } catch (e) { console.error(e); }
    })();
  }, [token]);

  const handleIndustryChange = async (newIndustry) => {
    if (!activeWorkspace?.id) return;
    setUpdatingIndustry(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${BACKEND_URL}/api/clients/${activeWorkspace.id}/industry`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ industry: newIndustry })
      });
      if (res.ok) {
        // Refetch health score con la nueva industria
        window.location.reload();
      }
    } catch (e) { console.error(e); }
    finally { setUpdatingIndustry(false); }
  };

  if (loading || !data) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <div className={`text-xl ${theme.text}`}>Calculando Ad Health Score...</div>
        </div>
      </DashboardLayout>
    );
  }

  const scoreColor = getScoreColor(data.account_score);
  const scoreLabel = getScoreLabel(data.account_score);
  const circumference = 2 * Math.PI * 60;
  const offset = circumference - (data.account_score / 100) * circumference;

  const scoreHexLight = scoreColor === 'success' ? '#22C55E' : scoreColor === 'warning' ? '#EAB308' : '#EF4444';

  const pillarMeta = {
    creatives: { label: 'Creativos', icon: <Sparkles className="w-4 h-4" /> },
    performance: { label: 'Performance', icon: <TrendingUp className="w-4 h-4" /> },
    audience: { label: 'Audiencia', icon: <Users className="w-4 h-4" /> },
    spend: { label: 'Gasto', icon: <DollarSign className="w-4 h-4" /> },
  };

  const pillarColor = (score) => {
    if (score >= 71) return 'text-success';
    if (score >= 41) return 'text-warning';
    return 'text-error';
  };

  const statusBadge = (status) => {
    if (status === 'healthy') return 'bg-success/20 text-success border-success/30';
    if (status === 'needs_improvement') return 'bg-warning/20 text-warning border-warning/30';
    return 'bg-error/20 text-error border-error/30';
  };

  const statusLabel = (status) => {
    if (status === 'healthy') return 'Saludable';
    if (status === 'needs_improvement') return 'Mejorable';
    return 'Crítico';
  };

  const insightStyle = (type) => {
    if (type === 'critical') return {
      wrap: 'border-l-4 border-error bg-error/10',
      title: 'text-error',
      icon: <AlertTriangle className="w-5 h-5 text-error" />,
    };
    if (type === 'warning') return {
      wrap: 'border-l-4 border-warning bg-warning/10',
      title: 'text-warning',
      icon: <TrendingDown className="w-5 h-5 text-warning" />,
    };
    return {
      wrap: 'border-l-4 border-success bg-success/10',
      title: 'text-success',
      icon: <TrendingUp className="w-5 h-5 text-success" />,
    };
  };

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="health-score-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex items-start justify-between gap-4 flex-wrap"
        >
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ai-glow to-brand-meta flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className={`font-heading font-bold text-4xl ${theme.text}`}>Ad Health Score</h1>
              <p className={`text-sm ${theme.textSecondary}`}>
                {activeWorkspace?.name ? `Cuenta: ${activeWorkspace.name}` : 'Diagnóstico inteligente de tus campañas'}
              </p>
            </div>
          </div>
          <div className="w-48">
            {/* Selector de fecha movido al layout global */}
          </div>
        </motion.div>

        {/* Tabs de plataforma */}
        <div className="platform-tabs flex items-center gap-2 mb-4">
          <button
            onClick={() => setPlatform('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${platform === 'all' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
          >
            🌐 Todas las plataformas
          </button>
          <button
            onClick={() => setPlatform('meta')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${platform === 'meta' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
          >
            📘 Meta Ads
          </button>
          <button
            onClick={() => setPlatform('google')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${platform === 'google' ? 'bg-orange-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
          >
            🟠 Google Ads
          </button>
        </div>

        {/* Banner de industria */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${theme.glassCard} rounded-xl p-4 mb-4 flex items-center justify-between flex-wrap gap-3`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-xl">
              📊
            </div>
            <div>
              <p className={`text-xs ${theme.textSecondary} uppercase tracking-wide`}>Benchmarks de industria</p>
              <p className={`text-lg font-semibold ${theme.text}`}>
                {data.industry_name || 'General'}
                {!data.industry && <span className={`ml-2 text-xs ${theme.textSecondary}`}>(sin asignar)</span>}
              </p>
            </div>
          </div>
          {industries.length > 0 && (
            <div className="flex items-center gap-2">
              <span className={`text-xs ${theme.textSecondary}`}>Cambiar:</span>
              <select
                value={data.industry || ''}
                onChange={(e) => handleIndustryChange(e.target.value)}
                disabled={updatingIndustry}
                className={`${theme.input || ''} px-3 py-1.5 rounded-lg text-sm border ${theme.border} cursor-pointer bg-white dark:bg-slate-800`}
              >
                <option value="">Sin industria</option>
                {industries.map(ind => (
                  <option key={ind.industry_key} value={ind.industry_key}>
                    {ind.industry_name}
                  </option>
                ))}
              </select>
              {updatingIndustry && <span className="text-xs">⏳</span>}
            </div>
          )}
        </motion.div>

        {/* HERO: Score + Pilares */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${theme.glassCard} rounded-2xl p-8 mb-6`}
        >
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-8 items-center">
            {/* Score circular */}
            <div className="text-center">
              <div className="relative w-[160px] h-[160px] mx-auto">
                <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
                  <circle cx="70" cy="70" r="60" fill="none"
                    stroke={theme.isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB'}
                    strokeWidth="10" />
                  <circle cx="70" cy="70" r="60" fill="none"
                    stroke={scoreHexLight}
                    strokeWidth="10" strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    style={{ transition: 'stroke-dashoffset 0.8s ease-out' }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className={`text-5xl font-bold ${theme.text} leading-none`}>{data.account_score}</div>
                  <div className={`text-xs ${theme.textSecondary} mt-1`}>de 100</div>
                </div>
              </div>
              <div className="mt-3">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${statusBadge(data.status)}`}>
                  {scoreLabel}
                </span>
              </div>
            </div>

            {/* Narrativa + Pilares */}
            <div>
              <p className={`${theme.text} text-base leading-relaxed mb-5`}>
                {data.narrative}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(data.pillars).map(([key, pillar]) => (
                  <div key={key}
                    title={pillar.description && pillar.detail ? `${pillar.description}\n\n${pillar.detail}` : ''}
                    className={`p-3 rounded-lg border ${theme.border} ${theme.isDark ? 'bg-white/[0.03]' : 'bg-gray-50'} cursor-help hover:shadow-md transition-shadow relative group`}>
                    <div className={`flex items-center gap-2 text-xs ${theme.textSecondary} mb-1`}>
                      {pillarMeta[key].icon}
                      <span>{pillarMeta[key].label}</span>
                    </div>
                    <div className={`text-2xl font-semibold ${pillarColor(pillar.score)}`}>
                      {pillar.score}
                    </div>
                    {pillar.description && (
                      <div className={`text-[10px] ${theme.textSecondary} mt-1 line-clamp-2 leading-tight`}>
                        {pillar.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* QUICK WINS */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl p-6 mb-6 border border-success/30 bg-success/10"
        >
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-success" />
            <h3 className={`font-semibold text-lg ${theme.text}`}>
              {data.quick_wins.length} acciones que podés hacer hoy
            </h3>
          </div>
          <div className="space-y-2">
            {data.quick_wins.map((qw) => (
              <div key={qw.id}
                className={`flex items-start gap-3 p-3 rounded-lg ${theme.isDark ? 'bg-white/[0.03]' : 'bg-white'} border ${theme.border}`}>
                <ArrowRight className="w-4 h-4 text-success mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <p className={`text-sm font-medium ${theme.text}`}>{qw.action}</p>
                  <p className={`text-xs ${theme.textSecondary} mt-0.5`}>{qw.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CAMPAÑAS + INSIGHTS */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
          {/* Campañas */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`${theme.glassCard} rounded-2xl p-6`}
          >
            <h3 className={`font-semibold text-lg ${theme.text} mb-4`}>Score por campaña</h3>
            <div className="space-y-2">
              {data.campaigns
                .slice()
                .sort((a, b) => a.score - b.score)
                .map((c) => (
                  <div key={c.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${theme.border} ${theme.cardHover} transition-colors`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${statusBadge(c.status)}`}>
                      {c.score}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${theme.text} truncate`}>{c.name}</p>
                      <p className={`text-xs ${theme.textSecondary}`}>
                        ${c.spend_per_day}/día · ROAS {c.roas} · CTR {c.ctr}%
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded border ${statusBadge(c.status)} flex-shrink-0`}>
                      {statusLabel(c.status)}
                    </span>
                  </div>
                ))}
            </div>
          </motion.div>

          {/* Insights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`${theme.glassCard} rounded-2xl p-6`}
          >
            <h3 className={`font-semibold text-lg ${theme.text} mb-4`}>Diagnóstico priorizado</h3>
            <div className="space-y-3">
              {data.insights
                .slice()
                .sort((a, b) => b.priority - a.priority)
                .map((ins) => {
                  const s = insightStyle(ins.type);
                  return (
                    <div key={ins.id} className={`rounded-lg p-3 ${s.wrap}`}>
                      <div className="flex items-start gap-2 mb-1">
                        {s.icon}
                        <p className={`text-sm font-semibold ${s.title}`}>{ins.title}</p>
                      </div>
                      <p className={`text-xs ${theme.textSecondary} ml-7 mb-2 leading-relaxed`}>
                        {ins.description}
                      </p>
                      <div className="flex items-center gap-2 ml-7">
                        <CheckCircle2 className={`w-3.5 h-3.5 ${s.title}`} />
                        <p className={`text-xs font-medium ${s.title}`}>{ins.action}</p>
                      </div>
                    </div>
                  );
                })}
            </div>
          </motion.div>
        </div>

        {/* Footer informativo */}
        <div className={`mt-8 text-center text-xs ${theme.textSecondary}`}>
          <p>Análisis basado en datos de los {dateRange === '7d' ? 'últimos 7 días' : dateRange === '14d' ? 'últimos 14 días' : 'últimos 30 días'}. Los benchmarks se comparan contra el promedio de la industria.</p>
          {!data.is_real_data && <p className="mt-1 opacity-60">⚠️ Mostrando datos de ejemplo — endpoint /ai/health-score pendiente de implementar.</p>}
          {data.is_real_data && <p className="mt-1 opacity-60 text-green-600">✅ Datos reales · {data.total_conversions?.toLocaleString()} conversiones · CPA promedio ${data.avg_cpa?.toLocaleString()} · ${data.total_spent?.toLocaleString()} gastados</p>}
        </div>
      </div>
    </DashboardLayout>
  );
}
